/**
 * A switch which asks, and then applies itself at once.
 *
 * The moderation of a thread used to be a panel with a save button. Two
 * moderators reading the same page could each send back the three flags they
 * saw when they opened it, and whoever pressed save second silently undid the
 * first - a form remembers the page it was drawn on, not the one it is being
 * sent from. So each switch is now its own form, carrying one flag, sent the
 * moment it is ticked.
 *
 * Applying something the instant it is clicked needs a question in front of
 * it, and the question has to be able to be refused: a refusal puts the
 * control back the way it was, because nothing was sent.
 *
 * The wording arrives in attributes rather than out of a catalogue of its
 * own. The server has already translated it, and a question about hiding a
 * thread differs from one about showing it again by more than a word.
 *
 * Without scripts none of this is here: the checkbox is a checkbox, and the
 * button beside it - hidden as soon as this component runs - sends the form.
 */

import { askConfirmation } from "./modal";
import { allowUnload } from "./unsaved";
import { queryAll } from "../core/dom";
import { text } from "../core/i18n";

/** Fills the `{section}` of a question with what was actually picked. */
const PLACEHOLDER = /\{section\}/g;

/** Send `form` the way a press of its own button would. */
function send(form: HTMLFormElement): void {
    // The page is leaving with the change in hand, so nothing is unsaved.
    allowUnload();
    form.submit();
}

/**
 * Ask about a checkbox, and put it back where it was if the answer is no.
 *
 * The question depends on the direction: switching a thread out of sight and
 * bringing it back are not the same act, and one of the two is the one worth
 * stopping to think about.
 */
export function bindInstantToggle(box: HTMLInputElement): void {
    box.addEventListener("change", () => {
        const form = box.form;
        if (!form) {
            return;
        }

        const wanted = box.checked;
        const question = (wanted ? box.dataset["askOn"] : box.dataset["askOff"]) ?? "";
        const label = wanted ? box.dataset["labelOn"] : box.dataset["labelOff"];

        void askConfirmation(question || text("js.confirm.default", "Naozaj chcete pokračovať?"), {
            confirm: label ?? text("js.confirm.confirm", "Potvrdiť"),
            danger: box.dataset["danger"] !== undefined && wanted,
        }).then((agreed) => {
            if (!agreed) {
                box.checked = !wanted;

                return;
            }

            send(form);
        });
    });
}

/**
 * Ask about a choice, and put the previous one back if the answer is no.
 *
 * The control remembers what it was showing rather than reading it back from
 * the options: by the time the question is answered the browser has long
 * since moved the selection.
 */
export function bindInstantSelect(select: HTMLSelectElement): void {
    let previous = select.value;

    select.addEventListener("change", () => {
        const form = select.form;
        if (!form || select.value === previous) {
            return;
        }

        const chosen = select.selectedOptions[0];
        const question = (select.dataset["ask"] ?? "").replace(PLACEHOLDER, (chosen?.textContent ?? "").trim());

        void askConfirmation(question || text("js.confirm.default", "Naozaj chcete pokračovať?"), {
            confirm: select.dataset["label"] ?? text("js.confirm.confirm", "Potvrdiť"),
        }).then((agreed) => {
            if (!agreed) {
                select.value = previous;
                // The list painted over the control follows the value it
                // holds, and only if it is told that the value moved.
                select.dispatchEvent(new Event("change", { bubbles: true }));

                return;
            }

            previous = select.value;
            send(form);
        });
    });
}

/** Wire every control which applies itself, and take the fallbacks away. */
export function initInstantToggles(): void {
    for (const box of queryAll<HTMLInputElement>("input[type=checkbox][data-instant-toggle]")) {
        bindInstantToggle(box);
    }

    for (const select of queryAll<HTMLSelectElement>("select[data-instant-select]")) {
        bindInstantSelect(select);
    }

    // The button which sends the form by hand is for the page without this
    // script. It is gone the moment there is one.
    for (const fallback of queryAll<HTMLElement>("[data-instant-fallback]")) {
        fallback.hidden = true;
    }
}
